import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  UserCheck, Plus, Trash2, Phone, Calendar, BarChart3,
  Navigation, MessageCircle, DollarSign, Smartphone, Save
} from "lucide-react";
import { ConfirmAction } from "@/components/ConfirmAction";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import getApiUrl from "@/utils/api";

const API = getApiUrl();

function DriverPaymentEditor({ driver, onSaved }) {
  const [cashapp, setCashapp] = useState(driver.cashapp_tag || "");
  const [zelle, setZelle] = useState(driver.zelle_email || "");
  const [saving, setSaving] = useState(false);
  const dirty = cashapp !== (driver.cashapp_tag || "") || zelle !== (driver.zelle_email || "");

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(
        `${API}/admin/drivers/${driver.id}`,
        { cashapp_tag: cashapp, zelle_email: zelle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Payment handles updated");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t space-y-2">
      <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 mb-1">
        <DollarSign className="w-3.5 h-3.5" /> Payment Handles (for QR codes)
      </div>
      <div>
        <Label className="text-[11px]">CashApp $cashtag</Label>
        <Input
          value={cashapp}
          onChange={(e) => setCashapp(e.target.value)}
          placeholder="$EasyTransportJ1"
          className="h-8 text-xs"
          data-testid={`cashapp-input-${driver.id}`}
        />
      </div>
      <div>
        <Label className="text-[11px]">Zelle email <span className="text-gray-400">(optional — phone used by default)</span></Label>
        <Input
          value={zelle}
          onChange={(e) => setZelle(e.target.value)}
          placeholder="driver@email.com"
          className="h-8 text-xs"
          data-testid={`zelle-input-${driver.id}`}
        />
      </div>
      {dirty && (
        <Button
          size="sm"
          onClick={save}
          disabled={saving}
          className="w-full h-8 text-xs bg-blue-600 hover:bg-blue-700"
          data-testid={`save-driver-payment-${driver.id}`}
        >
          <Save className="w-3 h-3 mr-1" />
          {saving ? "Saving..." : "Save Payment Handles"}
        </Button>
      )}
    </div>
  );
}

export function DriversTabContent({
  drivers,
  showAddDriver, setShowAddDriver,
  newDriver, setNewDriver,
  addDriver,
  deleteDriver,
  toggleDriverAvailability,
  refresh,
}) {
  return (
    <Card className="border-0 shadow-lg rounded-2xl">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <UserCheck className="w-5 h-5" />
            Driver Management & Location Tracking
          </CardTitle>
          
          <Dialog open={showAddDriver} onOpenChange={setShowAddDriver}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Driver
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Driver</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Driver Name *</Label>
                  <Input value={newDriver.name} onChange={(e) => setNewDriver({...newDriver, name: e.target.value})} placeholder="e.g., John Smith" />
                </div>
                <div>
                  <Label>Phone Number *</Label>
                  <Input value={newDriver.phone} onChange={(e) => setNewDriver({...newDriver, phone: e.target.value})} placeholder="e.g., +1-608-123-4567" />
                </div>
                <div>
                  <Label>WhatsApp (if different)</Label>
                  <Input value={newDriver.whatsapp} onChange={(e) => setNewDriver({...newDriver, whatsapp: e.target.value})} placeholder="Leave blank to use phone number" />
                </div>
                <div>
                  <Label>Role</Label>
                  <Input value={newDriver.role} onChange={(e) => setNewDriver({...newDriver, role: e.target.value})} placeholder="e.g., Driver, Lead Driver" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="flex items-center gap-1"><Smartphone className="w-3 h-3" /> CashApp $tag <span className="text-xs text-gray-400">(opt.)</span></Label>
                    <Input
                      value={newDriver.cashapp_tag || ""}
                      onChange={(e) => setNewDriver({...newDriver, cashapp_tag: e.target.value})}
                      placeholder="$YourTag"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> Zelle email <span className="text-xs text-gray-400">(opt.)</span></Label>
                    <Input
                      value={newDriver.zelle_email || ""}
                      onChange={(e) => setNewDriver({...newDriver, zelle_email: e.target.value})}
                      placeholder="driver@email.com"
                    />
                  </div>
                </div>
                <Button onClick={addDriver} className="w-full bg-green-600 hover:bg-green-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Driver
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {drivers.map((driver) => (
            <Card key={driver.id} className={`border-2 ${driver.is_available ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${driver.is_available ? 'bg-green-600' : 'bg-gray-400'}`}>
                      {driver.name.charAt(0)}
                    </div>
                    <div>
                      <h4 className="font-bold">{driver.name}</h4>
                      <p className="text-sm text-muted-foreground">{driver.role}</p>
                    </div>
                  </div>
                  <Badge variant={driver.is_available ? "default" : "secondary"}>
                    {driver.is_available ? "Available" : "Unavailable"}
                  </Badge>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{driver.phone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>Today's Trips: <strong>{driver.today_assignments || 0}</strong></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-muted-foreground" />
                    <span>Total Trips: <strong>{driver.total_trips || 0}</strong></span>
                  </div>
                  {driver.current_location && (
                    <div className="flex items-center gap-2 text-green-600">
                      <Navigation className="w-4 h-4" />
                      <span>Location: Active</span>
                      <Button variant="link" size="sm" className="h-auto p-0" onClick={() => window.open(`https://waze.com/ul?ll=${driver.current_location.lat},${driver.current_location.lng}&navigate=yes`, '_blank')}>
                        View Map
                      </Button>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button variant={driver.is_available ? "outline" : "default"} size="sm" className="flex-1" onClick={() => toggleDriverAvailability(driver.id)}>
                    {driver.is_available ? "Set Unavailable" : "Set Available"}
                  </Button>
                  <a href={`https://wa.me/${driver.whatsapp}`} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm"><MessageCircle className="w-4 h-4" /></Button>
                  </a>
                  <ConfirmAction
                    title="Delete Driver?"
                    description={`Are you sure you want to delete driver ${driver.name}? This cannot be undone.`}
                    onConfirm={() => deleteDriver(driver.id)}
                    confirmText="Yes, Delete"
                    variant="destructive"
                  >
                    <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 hover:border-red-300" data-testid={`delete-driver-${driver.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </ConfirmAction>
                </div>

                {/* Payment handles editor (CashApp / Zelle for QR codes on the driver app) */}
                <DriverPaymentEditor driver={driver} onSaved={refresh} />
              </CardContent>
            </Card>
          ))}
        </div>
        
        {drivers.length === 0 && (
          <div className="text-center py-12">
            <UserCheck className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No drivers registered</p>
            <Button onClick={() => setShowAddDriver(true)} className="mt-4 bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Driver
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
